import { useEffect } from 'react';
import { isFeatureEnabled } from '@/config/featureFlags';

/**
 * useAppliedThemePreference — make the case-workspace shells resolve their theme
 * the SAME way the app's dashboards do, so light/dark stays consistent app-wide.
 *
 * WHY: theme is applied as `data-theme` on <html>. The DASHBOARDS resolve it via one
 * of two layouts, chosen by the WORKSPACE_SHELL flag (see AppLayout.tsx):
 *   - flag OFF (legacy AppLayout)  → default LIGHT, toggle persists to `cs-theme`.
 *   - flag ON  (WorkspaceShell)    → default DARK,  toggle persists to `cg-theme`.
 * The case shells (CaseWorkspaceShell for accountant/attorney/adjuster/data_specialist)
 * mount OUTSIDE both layouts, so nothing applied the right theme there — the page fell
 * back to the dark-first `:root` default even when the dashboards were light. Result:
 * light dashboards + dark case pages.
 *
 * This hook mirrors the dashboards' resolution exactly (same flag → same key → same
 * default), and — critically — RESPECTS a theme already on <html> (inherited when the
 * user navigates in from a dashboard within the SPA) instead of clobbering it. So:
 *   1. If <html> already has data-theme (inherited) → leave it.
 *   2. Else honor a saved toggle from the matching key (cs-theme or cg-theme).
 *   3. Else fall back to the same default the dashboards use for this flag state.
 * Read-only w.r.t. the preference: it never writes a theme key (there is no toggle on
 * these shells), so it cannot fight the dashboards' toggle or drift the stored value.
 */
export function useAppliedThemePreference(): void {
  useEffect(() => {
    try {
      const root = document.documentElement;

      // 1. Respect a theme inherited from the layout the user came from (SPA nav).
      const existing = root.getAttribute('data-theme');
      if (existing === 'light' || existing === 'dark') return;

      // Which layout do the dashboards use? (mirror AppLayout.tsx exactly)
      const env = (import.meta as any).env;
      const workspaceShellEnabled =
        isFeatureEnabled('WORKSPACE_SHELL') ||
        env?.VITE_WORKSPACE_SHELL === 'true' ||
        env?.VITE_WORKSPACE_SHELL === '1';

      // 2. Honor a saved toggle from the key that layout writes.
      const key = workspaceShellEnabled ? 'cg-theme' : 'cs-theme';
      const saved = localStorage.getItem(key);
      const theme =
        saved === 'light' || saved === 'dark'
          ? saved
          : // 3. Same default the dashboards fall back to for this flag state.
            workspaceShellEnabled
            ? 'dark'
            : 'light';

      root.setAttribute('data-theme', theme);
    } catch {
      /* storage / env blocked — keep whatever the CSS :root default is */
    }
  }, []);
}

export default useAppliedThemePreference;
